import React, { useCallback, useMemo, useState } from "react";
import { DrilldownList, SearchActionsToolbar, makeFilterToolbarProps } from "@/components/UI/DrilldownList";
import { DrilldownDepthContext } from "@/components/UI/DrilldownList/context/DrilldownDepthContext";
import { useGetTopicClusteringTree } from "@/utils/react-query-hooks/aiAnnotation";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { resolveTopicClusteringLens } from "@okf/ootils";
import { useInFlightToggle } from "@/utils/customHooks/useInFlightToggle";
import { useListControls } from "@/utils/customHooks/useListControls";

/**
 * "Topics" filter (split-mode) — V3 filter comp for
 * filterType: "topicClusteringNodeType" (see V3_FILTER_COMPS).
 *
 * Renders the tenant's observation-clustering tree as a drilldown of
 * arbitrary depth (today's writer emits bucket → findings, but the level
 * builder recurses, so a deeper tree renders without changes here), same
 * shells as AnnoHierarchyFilter. Values are tree nodeIds; the BE handler
 * (getTopicClusteringNodeQuery) resolves each selected node to the
 * annotationIds under it, so selecting any non-leaf means "everything under
 * it" — folderSelectionMode "inclusive" keeps non-leaves both selectable and
 * drillable.
 *
 * Unlike the taxonomy hierarchy there's no per-level fetching: the whole tree
 * arrives in one GET (shared react-query cache with the Topics tab), so both
 * levels filter client-side.
 */

const getNodeDisplay = (node) => node?.label || "(unnamed)";

const toOption = (node, { icon, hasChildren }) => ({
  value: String(node.nodeId),
  display: getNodeDisplay(node),
  icon,
  hasChildren,
  // Threaded through so getChildLevel can read children without a lookup.
  node,
});

export const TopicClusterFilter = ({ filterConfig, value: selectedValues, onChange }) => {
  const { filterId, source = {} } = filterConfig;
  const platformConfigs = useGetQueryData("platformConfigs");
  const topicClusteringLens = resolveTopicClusteringLens({ platformConfigs_ai: platformConfigs?.ai, topicClusteringLens: source.topicClusteringLens });

  const { data: treeData, isLoading } = useGetTopicClusteringTree({ topicClusteringLens });
  const tree = treeData?.tree || [];

  const value = Array.isArray(selectedValues) ? selectedValues : [];

  // Client-side search (whole tree is already on the client). The top level
  // has its own state; all deeper panels share one, cleared on each drill so
  // text typed in one panel doesn't ghost-filter a sibling panel.
  const [searchInputBuckets, setSearchInputBuckets] = useState("");
  const [searchInputChildren, setSearchInputChildren] = useState("");

  const toggle = useInFlightToggle();
  const handleToggle = useCallback(
    (option) => {
      const newValues = toggle({
        key: filterId,
        fallbackValues: value,
        option,
        buildEntry: (o, id) => ({ value: id, display: o.display }),
      });
      onChange(filterId, newValues);
    },
    [filterId, onChange, toggle, value]
  );
  const onListChange = useCallback((next) => onChange(filterId, next), [filterId, onChange]);

  const makeNodeOption = useCallback(
    (node) =>
      toOption(node, {
        icon: node.children?.length ? "FolderSimple" : "File",
        hasChildren: !!node.children?.length,
      }),
    []
  );

  const bucketOptions = useMemo(() => tree.map(makeNodeOption), [tree, makeNodeOption]);

  const bucketsCtl = useListControls({
    items: bucketOptions,
    value,
    onChange: onListChange,
    getKey: (o) => o.value,
    getDisplay: (o) => o.display,
  });

  // Recursive: a child level's non-leaf options hand this same builder to
  // DrilldownLevel, so the drilldown follows the tree to any depth. Plain
  // function (not useCallback) so it can reference itself without a
  // self-dependency; recreated per render like the rest of the level config.
  const getNodeChildLevel = (parentOption) => {
    const childOptions = (parentOption.node?.children || [])
      .map(makeNodeOption)
      .filter(
        (o) =>
          !searchInputChildren ||
          o.display.toLowerCase().includes(searchInputChildren.toLowerCase())
      );
    return {
      options: childOptions,
      value,
      isMulti: true,
      onChange: handleToggle,
      getChildLevel: getNodeChildLevel,
      onDrilldown: () => setSearchInputChildren(""),
      emptyMessage: "No findings in this group",
      headerContent: (
        <SearchActionsToolbar
          search={{ value: searchInputChildren, onChange: setSearchInputChildren }}
        />
      ),
    };
  };

  const getBucketsLevel = () => ({
    options: bucketsCtl.orderedItems.filter(
      (o) =>
        !searchInputBuckets ||
        o.display.toLowerCase().includes(searchInputBuckets.toLowerCase())
    ),
    value,
    isMulti: true,
    isLoading,
    onChange: handleToggle,
    getChildLevel: getNodeChildLevel,
    onDrilldown: () => setSearchInputChildren(""),
    emptyMessage: "No topics yet — the tree appears after AI annotation runs",
    showSelectedFirst: bucketsCtl.showSelectedFirst,
    headerContent: (
      <SearchActionsToolbar
        {...makeFilterToolbarProps(bucketsCtl, {
          searchValue: searchInputBuckets,
          onSearch: setSearchInputBuckets,
        })}
      />
    ),
  });

  const metaOptions = useMemo(
    () => [
      {
        value: "topicClusters",
        display: filterConfig.display || "Topics",
        icon: "FolderSimple",
        hasChildren: true,
      },
    ],
    [filterConfig.display]
  );

  return (
    <DrilldownDepthContext.Provider value={0}>
      <DrilldownList
        options={metaOptions}
        isMulti={false}
        getChildLevel={getBucketsLevel}
        folderSelectionMode={"inclusive"}
      />
    </DrilldownDepthContext.Provider>
  );
};
